import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import adminGameService from '../../../utils/adminGameService';
import DirectoryUploadModal from '../../../components/DirectoryUploadModal';

const GameUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [uploadStep, setUploadStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [gameType, setGameType] = useState('webgpu-files');
  const [gameFiles, setGameFiles] = useState({
    dataFile: null,
    wasmFile: null,
    frameworkFile: null,
    loaderFile: null,
    backgroundImage: null
  });
  const [hasDirectoryData, setHasDirectoryData] = useState(false);
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);
  const directoryUploadDataRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    studio: '',
    category: '',
    description: '',
    contentRating: 'E',
    tier: 'premium',
    price: '0',
    revenueShare: 70,
    gamedistribution_url: '',
    systemRequirements: {
      minRam: '4',
      minStorage: '2',
      webGpuSupport: true
    }
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(null);
  const [studios, setStudios] = useState([]);
  const [loadingStudios, setLoadingStudios] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const screenshotInputRef = useRef(null);

  // Load studios and categories when modal opens
  useEffect(() => {
    if (isOpen) {
      if (studios.length === 0) {
        loadStudios();
      }
      if (categories.length === 0) {
        loadCategories();
      }
    }
  }, [isOpen]);

  const loadStudios = async () => {
    setLoadingStudios(true);
    try {
      const result = await adminGameService.getGamingStudios();
      if (result.success) {
        setStudios(result.data);
      } else {
        console.error('Failed to load studios:', result.error);
      }
    } catch (error) {
      console.error('Error loading studios:', error);
    } finally {
      setLoadingStudios(false);
    }
  };

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const result = await adminGameService.getGameCategories();
      if (result.success) {
        // Transform categories to the format expected by the Select component
        const categoryOptions = result.data.map(category => ({
          value: category.name,
          label: category.name
        }));
        setCategories(categoryOptions);
      } else {
        console.error('Error loading categories:', result.error);
        // Fallback to hardcoded categories if database fetch fails
        setCategories([
          { value: 'Action', label: 'Action' },
          { value: 'Adventure', label: 'Adventure' },
          { value: 'Puzzle', label: 'Puzzle' },
          { value: 'Strategy', label: 'Strategy' },
          { value: 'Racing', label: 'Racing' },
          { value: 'Sports', label: 'Sports' },
          { value: 'Simulation', label: 'Simulation' },
          { value: 'RPG', label: 'RPG' }
        ]);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      // Fallback to hardcoded categories if there's an exception
      setCategories([
        { value: 'Action', label: 'Action' },
        { value: 'Adventure', label: 'Adventure' },
        { value: 'Puzzle', label: 'Puzzle' },
        { value: 'Strategy', label: 'Strategy' },
        { value: 'Racing', label: 'Racing' },
        { value: 'Sports', label: 'Sports' },
        { value: 'Simulation', label: 'Simulation' },
        { value: 'RPG', label: 'RPG' }
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddNewStudio = () => {
    // Open admin-studio-management page in a new tab
    window.open('/admin-studio-management', '_blank');
  };

  const contentRatings = [
    { value: 'E', label: 'Everyone' },
    { value: 'E10+', label: 'Everyone 10+' },
    { value: 'T', label: 'Teen' },
    { value: 'M', label: 'Mature 17+' },
    { value: 'AO', label: 'Adults Only 18+' }
  ];

  const tierOptions = [
    { value: 'free', label: 'Free' },
    { value: 'premium', label: 'Premium' }
  ];

  const handleWebGPUFileSelect = (event, fileType) => {
    const file = event.target.files[0];
    if (file) {
      setGameFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };

  const handleImageFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    }));
    
    setUploadedImages(prev => [...prev, ...newImages]);
    
    // Auto-select first image as thumbnail if none selected
    if (selectedThumbnailIndex === null && newImages.length > 0) {
      setSelectedThumbnailIndex(uploadedImages.length);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // Adjust thumbnail selection if needed
      if (selectedThumbnailIndex === index) {
        setSelectedThumbnailIndex(newImages.length > 0 ? 0 : null);
      } else if (selectedThumbnailIndex > index) {
        setSelectedThumbnailIndex(selectedThumbnailIndex - 1);
      }
      return newImages;
    });
  };

  const setAsThumbnail = (index) => {
    setSelectedThumbnailIndex(index);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    
    // Auto-detect file types based on extensions
    files.forEach(file => {
      const fileName = file.name.toLowerCase();
      if (fileName.includes('.data.br') || fileName.includes('.data')) {
        setGameFiles(prev => ({ ...prev, dataFile: file }));
      } else if (fileName.includes('.wasm.br') || fileName.includes('.wasm')) {
        setGameFiles(prev => ({ ...prev, wasmFile: file }));
      } else if (fileName.includes('.framework.js.br') || fileName.includes('.framework.js')) {
        setGameFiles(prev => ({ ...prev, frameworkFile: file }));
      } else if (fileName.includes('.loader.js')) {
        setGameFiles(prev => ({ ...prev, loaderFile: file }));
      } else if (file.type.startsWith('image/')) {
        if (fileName.includes('background') || fileName.includes('cover')) {
          setGameFiles(prev => ({ ...prev, backgroundImage: file }));
        } else if (!thumbnailFile) {
          setThumbnailFile(file);
        } else {
          setScreenshotFiles(prev => [...prev, file]);
        }
      }
    });
    
    if (files.length > 0) {
      setUploadStep(2);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDirectoryUpload = (uploadData) => {
    // CRITICAL FIX: Don't store File objects in React state as they get serialized
    // Instead, store them in a ref to preserve the File prototype
    directoryUploadDataRef.current = uploadData;
    setShowDirectoryModal(false);
    setUploadStep(2);
    // Set a flag to indicate we have upload data
    setHasDirectoryData(true);
  };

  const openDirectoryModal = () => {
    setShowDirectoryModal(true);
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setUploadStep(3);
    setUploadProgress(10);

    try {
      // Convert gameType to correct database value and handle URLs appropriately
      let dbGameType = 'webgpu'; // default
      let gameStructureType = 'legacy'; // default
      
      if (gameType === 'gamedistribution') {
        dbGameType = 'gamedistribution';
        gameStructureType = 'external';
      } else if (gameType.includes('react')) {
        dbGameType = 'react';
        gameStructureType = 'directory';
      } else if (gameType.includes('html5')) {
        dbGameType = 'html5'; // Fix: Use 'html5' not 'react'
        gameStructureType = 'directory';
      } else if (gameType.includes('directory')) {
        dbGameType = 'webgpu';
        gameStructureType = 'directory';
      } else if (gameType.includes('webgpu')) {
        dbGameType = 'webgpu';
        gameStructureType = 'legacy';
      }

      // For GameDistribution games, parse the URL to get the embedded URL
      let processedGameDistributionUrl = formData.gamedistribution_url;
      if (gameType === 'gamedistribution' && formData.gamedistribution_url) {
        try {
          setUploadProgress(15);
          const proxyUrl = `/api/gamedistribution-proxy?url=${encodeURIComponent(formData.gamedistribution_url)}&gameTitle=${encodeURIComponent(formData.title)}`;
          const proxyResponse = await fetch(proxyUrl);
          if (proxyResponse.ok) {
            const proxyData = await proxyResponse.json();
            processedGameDistributionUrl = proxyData.embeddedUrl;
            console.log('Processed GameDistribution URL:', processedGameDistributionUrl);
          } else {
            console.warn('Failed to process GameDistribution URL, using original URL');
          }
        } catch (error) {
          console.warn('Error processing GameDistribution URL:', error);
          // Continue with original URL if processing fails
        }
      }

      // First create the game record
      const gameData = {
        title: formData.title,
        studio: formData.studio,
        category: formData.category,
        description: formData.description,
        contentRating: formData.contentRating,
        tier: formData.tier,
        price: formData.price,
        revenueShare: formData.revenueShare,
        // Flatten system requirements for adminGameService
        systemRequirements: {
          minRam: formData.systemRequirements.minRam,
          minStorage: formData.systemRequirements.minStorage,
          webGpuSupport: formData.systemRequirements.webGpuSupport
        },
        game_type: dbGameType,
        game_structure_type: gameStructureType,
        status: 'draft'
      };

      // Add GameDistribution URL if applicable
      if (gameType === 'gamedistribution') {
        gameData.gamedistribution_url = processedGameDistributionUrl;
      }
      
      console.log('Creating game with type:', dbGameType, 'structure:', gameStructureType, 'and data:', gameData);

      setUploadProgress(20);
      const createResult = await adminGameService.createGame(gameData);
      
      if (!createResult.success) {
        throw new Error(createResult.error);
      }

      const gameId = createResult.data.id;
      setUploadProgress(30);

      // Handle directory uploads
      if (hasDirectoryData && gameType.includes('directory')) {
        const directoryResult = await adminGameService.uploadDirectoryGame(gameId, directoryUploadDataRef.current);
        if (!directoryResult.success) {
          throw new Error(directoryResult.error);
        }
        setUploadProgress(60);

        // Update game with directory information
        const updateData = {
          game_directory_path: directoryResult.data.directoryPath,
          game_subdirectory: directoryResult.data.gameSubdirectory || '',
          game_manifest: directoryResult.data.manifest
        };

        await adminGameService.updateGame(gameId, updateData);
      }
      // Handle WebGPU assets upload
      else if (gameType.includes('webgpu') && Object.values(gameFiles).some(file => file)) {
        const assetResult = await adminGameService.uploadWebGPUAssets(gameId, gameFiles);
        if (!assetResult.success) {
          throw new Error(assetResult.error);
        }
        setUploadProgress(60);

        // Update game with asset URLs
        const webgpuConfig = {
          companyName: formData.studio,
          productName: formData.title.toLowerCase().replace(/\s+/g, '-'),
          productVersion: "1.0",
          streamingAssetsUrl: "StreamingAssets"
        };

        const updateData = {
          webgpu_data_url: assetResult.data.dataFile,
          webgpu_wasm_url: assetResult.data.wasmFile,
          webgpu_framework_url: assetResult.data.frameworkFile,
          webgpu_loader_url: assetResult.data.loaderFile,
          webgpu_config: webgpuConfig
        };

        if (assetResult.data.backgroundImage) {
          updateData.cover_image_url = assetResult.data.backgroundImage;
          webgpuConfig.backgroundUrl = assetResult.data.backgroundImage;
        }

        await adminGameService.updateGame(gameId, updateData);
      } else {
        // For non-WebGPU games, skip asset upload
        setUploadProgress(60);
      }

      setUploadProgress(80);

      // Upload images using the consolidated system
      const images = {};
      if (uploadedImages.length > 0) {
        // Set thumbnail
        if (selectedThumbnailIndex !== null && uploadedImages[selectedThumbnailIndex]) {
          images.thumbnail = uploadedImages[selectedThumbnailIndex].file;
        }
        
        // Set screenshots (all images except the selected thumbnail)
        uploadedImages.forEach((image, index) => {
          if (index !== selectedThumbnailIndex) {
            images[`screenshot_${index}`] = image.file;
          }
        });
      }

      if (Object.keys(images).length > 0) {
        const imageResult = await adminGameService.uploadGameImages(gameId, images);
        if (imageResult.success) {
          // Update game with thumbnail and screenshots
          const updateData = {};
          if (imageResult.data.thumbnail) {
            updateData.cover_image_url = imageResult.data.thumbnail;
          }
          
          // Collect screenshot URLs - ensure they're stored as JSONB array
          const screenshotUrls = [];
          Object.entries(imageResult.data).forEach(([key, url]) => {
            if (key.startsWith('screenshot_') && url) {
              screenshotUrls.push(url);
            }
          });
          
          if (screenshotUrls.length > 0) {
            updateData.screenshots = JSON.stringify(screenshotUrls);
          }
          
          if (Object.keys(updateData).length > 0) {
            await adminGameService.updateGame(gameId, updateData);
          }
        }
      }

      setUploadProgress(100);

      // Get the final game data
      const finalResult = await adminGameService.getAllGames();
      const newGame = finalResult.data?.find(g => g.id === gameId);

      setTimeout(() => {
        onUpload(newGame || createResult.data);
        handleClose();
      }, 1000);

    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
      setIsUploading(false);
      setUploadStep(2);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setUploadStep(1);
    setUploadProgress(0);
    setIsUploading(false);
    setGameType('webgpu-files');
    setGameFiles({
      dataFile: null,
      wasmFile: null,
      frameworkFile: null,
      loaderFile: null,
      backgroundImage: null
    });
    // Clean up consolidated image system
    uploadedImages.forEach(image => {
      if (image.preview) {
        URL.revokeObjectURL(image.preview);
      }
    });
    setUploadedImages([]);
    setSelectedThumbnailIndex(null);
    setFormData({
      title: '',
      studio: '',
      category: '',
      description: '',
      contentRating: 'E',
      tier: 'premium',
      price: '0',
      revenueShare: 70,
      gamedistribution_url: '',
      systemRequirements: {
        minRam: '4',
        minStorage: '2',
        webGpuSupport: true
      }
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-lg shadow-gaming-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Upload New Game</h2>
          <Button variant="ghost" size="sm" iconName="X" onClick={handleClose} />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Game Type Selection and Upload */}
          {uploadStep === 1 && (
            <div className="space-y-6">
              {/* Game Type Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Game Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="gameType"
                      value="webgpu-files"
                      checked={gameType === 'webgpu-files'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <div>
                      <div className="font-medium text-foreground">WebGPU Files</div>
                      <div className="text-sm text-muted-foreground">Individual Unity files</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="gameType"
                      value="webgpu-directory"
                      checked={gameType === 'webgpu-directory'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <div>
                      <div className="font-medium text-foreground">WebGPU Directory</div>
                      <div className="text-sm text-muted-foreground">Unity build folder</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="gameType"
                      value="react-directory"
                      checked={gameType === 'react-directory'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <div>
                      <div className="font-medium text-foreground">React Directory</div>
                      <div className="text-sm text-muted-foreground">HTML5 React build</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="gameType"
                      value="html5-directory"
                      checked={gameType === 'html5-directory'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <div>
                      <div className="font-medium text-foreground">HTML5 Directory</div>
                      <div className="text-sm text-muted-foreground">Construct 2/3 build</div>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="radio"
                      name="gameType"
                      value="gamedistribution"
                      checked={gameType === 'gamedistribution'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <div>
                      <div className="font-medium text-foreground">GameDistribution</div>
                      <div className="text-sm text-muted-foreground">External embedded games</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* GameDistribution URL Input */}
              {gameType === 'gamedistribution' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                          GameDistribution Integration
                        </h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                          Copy the game URL from GameDistribution.com and paste it below. We'll automatically extract the embedded game URL.
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          Example: https://gamedistribution.com/games/labubu-merge/
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Input
                    label="GameDistribution URL"
                    type="url"
                    placeholder="https://gamedistribution.com/games/your-game/"
                    value={formData.gamedistribution_url}
                    onChange={(e) => handleInputChange('gamedistribution_url', e.target.value)}
                    required
                  />
                  
                  <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertTriangle" size={16} className="text-yellow-600 dark:text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Games from GameDistribution may contain ads
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Directory Upload for WebGPU, React, and HTML5 */}
              {(gameType.includes('directory')) && (
                <div className="space-y-6">
                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                          Directory Upload
                        </h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                          Upload your complete game directory with all files and subdirectories preserved.
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          {gameType === 'webgpu-directory' 
                            ? 'Unity WebGPU build folder with Build/, TemplateData/, and index.html'
                            : gameType === 'react-directory'
                            ? 'React build folder with assets/, index.html, and other build files'
                            : 'HTML5 game folder with index.html, images/, audio/, and game scripts'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {hasDirectoryData ? (
                    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400" />
                        <h4 className="font-medium text-green-900 dark:text-green-100">
                          Directory Selected
                        </h4>
                      </div>
                      <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                        <p>Files: {directoryUploadDataRef.current?.manifest?.length || 0}</p>
                        <p>Game Type: {directoryUploadDataRef.current?.gameType}</p>
                        <p>Structure: {directoryUploadDataRef.current?.structureType}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openDirectoryModal}
                        className="mt-3"
                        iconName="FolderOpen"
                      >
                        Change Directory
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={openDirectoryModal}
                    >
                      <Icon name="FolderOpen" size={64} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Select Game Directory
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Click to browse and select your complete game directory
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {gameType === 'webgpu-directory' 
                          ? 'Select your Unity WebGPU build folder'
                          : gameType === 'react-directory'
                          ? 'Select your React build folder (dist/ or build/)'
                          : 'Select your HTML5 game folder'
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* WebGPU File Upload */}
              {gameType === 'webgpu-files' && (
                <div className="space-y-6">
                  <div
                    className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <Icon name="Upload" size={64} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Upload WebGPU Game Files
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Drag and drop all your Unity WebGPU build files here
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Required: .data.br, .wasm.br, .framework.js.br, .loader.js files
                    </p>
                  </div>

                  {/* Individual file inputs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Data File (.data.br)
                      </label>
                      <input
                        type="file"
                        accept=".br,.data"
                        onChange={(e) => handleWebGPUFileSelect(e, 'dataFile')}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                      />
                      {gameFiles.dataFile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ✓ {gameFiles.dataFile.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        WASM File (.wasm.br)
                      </label>
                      <input
                        type="file"
                        accept=".br,.wasm"
                        onChange={(e) => handleWebGPUFileSelect(e, 'wasmFile')}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                      />
                      {gameFiles.wasmFile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ✓ {gameFiles.wasmFile.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Framework File (.framework.js.br)
                      </label>
                      <input
                        type="file"
                        accept=".br,.js"
                        onChange={(e) => handleWebGPUFileSelect(e, 'frameworkFile')}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                      />
                      {gameFiles.frameworkFile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ✓ {gameFiles.frameworkFile.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Loader File (.loader.js)
                      </label>
                      <input
                        type="file"
                        accept=".js"
                        onChange={(e) => handleWebGPUFileSelect(e, 'loaderFile')}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                      />
                      {gameFiles.loaderFile && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ✓ {gameFiles.loaderFile.name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Optional background image */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Background Image (Optional)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleWebGPUFileSelect(e, 'backgroundImage')}
                      className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                    />
                    {gameFiles.backgroundImage && (
                      <p className="text-xs text-muted-foreground mt-1">
                        ✓ {gameFiles.backgroundImage.name}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={() => setUploadStep(2)}
                  disabled={
                    gameType === 'webgpu-files' 
                      ? (!gameFiles.dataFile || !gameFiles.wasmFile || !gameFiles.frameworkFile || !gameFiles.loaderFile)
                      : gameType === 'gamedistribution'
                      ? !formData.gamedistribution_url
                      : gameType.includes('directory')
                      ? !hasDirectoryData
                      : false
                  }
                  iconName="ArrowRight"
                >
                  Next: Game Information
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Game Information */}
          {uploadStep === 2 && (
            <div className="space-y-6">
              {/* Files Summary */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">
                  {gameType === 'gamedistribution' ? 'Game Configuration:' : 'Uploaded Files:'}
                </h4>
                {gameType === 'gamedistribution' ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-green-500" />
                      <span className="text-foreground">Game Type: GameDistribution</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-green-500" />
                      <span className="text-foreground">URL: {formData.gamedistribution_url}</span>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(gameFiles).map(([key, file]) => (
                      file && (
                        <div key={key} className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span className="text-foreground">{file.name}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Game Title"
                  type="text"
                  placeholder="Enter game title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />

                <div className="space-y-2">
                  <Select
                    label="Studio"
                    placeholder={loadingStudios ? "Loading studios..." : "Select studio"}
                    options={[
                      ...studios.map(studio => ({
                        value: studio.id,
                        label: studio.name
                      })),
                      {
                        value: 'add-new',
                        label: '+ Add New Studio'
                      }
                    ]}
                    value={formData.studio}
                    onChange={(value) => {
                      if (value === 'add-new') {
                        handleAddNewStudio();
                      } else {
                        handleInputChange('studio', value);
                      }
                    }}
                    disabled={loadingStudios}
                    required
                  />
                  {formData.studio === 'add-new' && (
                    <p className="text-xs text-muted-foreground">
                      Please create a new studio in the opened tab, then refresh this page to see it in the dropdown.
                    </p>
                  )}
                </div>

                <Select
                  label="Category"
                  placeholder={loadingCategories ? "Loading categories..." : "Select category"}
                  options={categories}
                  value={loadingCategories ? '' : formData.category}
                  onChange={(value) => handleInputChange('category', value)}
                  required
                  disabled={loadingCategories}
                  loading={loadingCategories}
                />

                <Select
                  label="Content Rating"
                  placeholder="Select rating"
                  options={contentRatings}
                  value={formData.contentRating}
                  onChange={(value) => handleInputChange('contentRating', value)}
                  required
                />

                <Select
                  label="Tier"
                  placeholder="Select tier"
                  options={tierOptions}
                  value={formData.tier}
                  onChange={(value) => handleInputChange('tier', value)}
                  required
                />

                <Input
                  label="Price (USD)"
                  type="number"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  disabled={formData.tier === 'free'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  className="w-full h-24 px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Enter game description..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div>

              {/* System Requirements */}
              <div>
                <h4 className="font-medium text-foreground mb-3">System Requirements</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Minimum RAM (GB)"
                    type="number"
                    placeholder="4"
                    value={formData.systemRequirements.minRam}
                    onChange={(e) => handleInputChange('systemRequirements', {
                      ...formData.systemRequirements,
                      minRam: e.target.value
                    })}
                  />
                  <Input
                    label="Minimum Storage (GB)"
                    type="number"
                    placeholder="2"
                    value={formData.systemRequirements.minStorage}
                    onChange={(e) => handleInputChange('systemRequirements', {
                      ...formData.systemRequirements,
                      minStorage: e.target.value
                    })}
                  />
                </div>
              </div>

              {/* Media Upload */}
              <div>
                <h4 className="font-medium text-foreground mb-3">Game Images</h4>
                <div className="space-y-4">
                  {/* Image Upload Area */}
                  <div>
                    <input
                      ref={screenshotInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageFileSelect}
                      className="hidden"
                    />
                    <div
                      className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => screenshotInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files);
                        const imageFiles = files.filter(file => file.type.startsWith('image/'));
                        if (imageFiles.length > 0) {
                          const newImages = imageFiles.map(file => ({
                            file,
                            preview: URL.createObjectURL(file),
                            name: file.name,
                            size: file.size
                          }));
                          setUploadedImages(prev => [...prev, ...newImages]);
                          
                          // Auto-select first image as thumbnail if none selected
                          if (selectedThumbnailIndex === null && newImages.length > 0) {
                            setSelectedThumbnailIndex(uploadedImages.length);
                          }
                        }
                      }}
                    >
                      <Icon name="Images" size={32} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium text-foreground mb-1">Upload Game Images</p>
                      <p className="text-xs text-muted-foreground">
                        Drag and drop images or click to browse (multiple files supported)
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        After uploading, click on any image to set it as the thumbnail
                      </p>
                    </div>
                  </div>
                  
                  {/* Images Preview Grid */}
                  {uploadedImages.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">
                          Uploaded Images ({uploadedImages.length})
                        </p>
                        {selectedThumbnailIndex !== null && (
                          <p className="text-xs text-muted-foreground">
                            Thumbnail: {uploadedImages[selectedThumbnailIndex]?.name}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {uploadedImages.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className={`aspect-video rounded-lg overflow-hidden bg-muted cursor-pointer transition-all ${
                              selectedThumbnailIndex === index ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'
                            }`}
                            onClick={() => setAsThumbnail(index)}
                            >
                              <Image
                                src={image.preview}
                                alt={`Image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="destructive"
                                size="sm"
                                iconName="X"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(index);
                                }}
                                title="Remove image"
                              />
                            </div>
                            <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="secondary"
                                size="sm"
                                iconName="Image"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAsThumbnail(index);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                title="Set as thumbnail"
                              />
                            </div>
                            {selectedThumbnailIndex === index && (
                              <div className="absolute bottom-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                                Thumbnail
                              </div>
                            )}
                            <div className="mt-1">
                              <p className="text-xs text-muted-foreground truncate">
                                {image.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {(image.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4">
                <Button variant="outline" onClick={() => setUploadStep(1)}>
                  Back
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!formData.title || !formData.studio || !formData.category}
                  iconName="Upload"
                >
                  Create Game
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Upload Progress */}
          {uploadStep === 3 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Upload" size={32} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Uploading Game...
              </h3>
              <p className="text-muted-foreground mb-6">
                Please wait while we upload your WebGPU game files and create the game entry
              </p>
              
              <div className="w-full bg-muted rounded-full h-3 mb-4">
                <div
                  className="bg-primary h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Uploading WebGPU assets and creating game...
                </span>
                <span className="text-foreground font-mono">
                  {uploadProgress}%
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Directory Upload Modal */}
      <DirectoryUploadModal
        isOpen={showDirectoryModal}
        onClose={() => setShowDirectoryModal(false)}
        onUpload={handleDirectoryUpload}
      />
    </div>
  );
};

export default GameUploadModal;
